import React, {useContext, useEffect, useState} from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthenticationApiCalls } from "../../apis/AuthenticationApiCalls";
import { YuJaCheckbox } from "../../components/standardization/YuJaCheckbox";
import { useLocalStorageNew } from "../../hooks/useLocalStorageNew";
import { ReactComponent as YuJaLogo } from "../../images/yujalogo.svg";
import { JOIN_PARTICIPANT_PATH, LOGIN_METHODS, PLATFORM_SETTINGS, SCREEN_TYPE, TEXTBOX, YUJA_DROPDOWN_SIZE } from "../../utils/constants";
import {checkAllEmpty, newPageLog, notifyError} from "../../utils/helpers";
import useQuery from "../../utils/queryparam";
import styles from "./Login.module.css";
import EyeClosed from "./images/EyeClosed.svg";
import EyeOpen from "./images/EyeOpen.svg";
import {ReactComponent as INVALID_ICON} from "./images/invalid-icon.svg"
import useWindowDimensions from "../../hooks/useWindowDimensions";
import {YuJaTooltip} from "../../components/standardization/YuJaTooltip";
import {useCSMAccessCheck} from "../../hooks/useCSMAccessCheck";
import YuJaSelect from "../../components/standardization/YuJaSelect";
import YuJaTextField from "../../components/standardization/YuJaTextField";
import {Button} from "@base-ui/react/button";
import {YuJaGlobalState} from "../../utils/LoadingContext";

export default function LoginPage() {
  const { t } = useTranslation();
  const { login, getSAMLRedirectURL } = AuthenticationApiCalls();
  const {isCSMSystem} = useCSMAccessCheck();
  const {globalHostResource: hostResource} = useContext(YuJaGlobalState);
  const {platformSetting, ssoConfig={}, imageURL, fullName, logoImageURL} = hostResource;
  const [,getSession] = useLocalStorageNew("session", {});
  const history = useHistory();
  const location = useLocation();
  const {type} = useWindowDimensions();
  const [keepSignIn, setKeepSignIn] = useState(false);
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [userNameError, setUserNameError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  // const [institutionName, setInstitutionName] = useState(YUJA_TITLE);
  const [passwordShown, setPasswordShown] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [setGuestSession, ] = useLocalStorageNew("guestSession", {});

  const SSO_LOGIN_ERROR_MSG_ARR = ["invalidParams", "parseError", "invalidSign", "invalidAssertion","invalidFirstName", "invalidLastName", "invalidEmail"];
  let errorParams = useQuery();
  const convertDefaultName = (name) => {
    if (name === LOGIN_METHODS.YUJA) {
      return t("auth.loginOptionYuja");
    } else if (name === LOGIN_METHODS.ADFS) {
      return t("auth.loginOptionAdfs");
    } else if (name === LOGIN_METHODS.SHIBBOLETH) {
      return t("auth.loginOptionShibboleth");
    } else if (name === LOGIN_METHODS.DCAM) {
      return t("auth.loginOptionDcam");
    } else if (name === LOGIN_METHODS.GOOGLE) {
      return t("auth.loginOptionGoogle");
    } else if (name === LOGIN_METHODS.AZURE) {
      return t("auth.loginOptionAzure");
    } else if (name === LOGIN_METHODS.ENTRA) {
      return "SSO - ENTRA";
    }
  }

  const [loginMethodOptions, setLoginMethodOptions] = useState(() => {
    const opts = Object.entries(LOGIN_METHODS).map((entry, index) => {
      return {
        defaultName: convertDefaultName(entry[1]),
        abbr: "",
        key: entry[0],
        val: entry[1],
        no: index + 1,
        enabled: entry[1] === LOGIN_METHODS.YUJA,
        selected: entry[1] === LOGIN_METHODS.YUJA,
      }
    });
    return opts;
  });

  const inlineStyles = {
    rowStyle: {display: "flex", alignItems: "center", columnGap: 5, rowGap: 10, flexWrap: "wrap"},
    inputBoxStyle: {fontSize: 15, height: 47, fontWeight: "400", padding: 12},
    linkStyle: {fontSize: 16, alignSelf: "center"},
  }

  // This will be triggered when some required params were not passed from the SAML Identity Provider
  // NameID, Given Name, Last Name and Email are mandatory
  useEffect(() => {
    if (errorParams.get("errorMsg")) {
      if (SSO_LOGIN_ERROR_MSG_ARR.includes(errorParams.get("errorMsg"))) {
        if(errorParams.get("errorMsg") === "invalidFirstName") {
          notifyError(t("auth.lmsGivenNameMissing"));
        } else if(errorParams.get("errorMsg") === "invalidLastName") {
          notifyError(t("auth.lmsLastNameMissing"));
        } else if(errorParams.get("errorMsg") === "invalidEmail") {
          notifyError(t("auth.lmsEmailMissing"));
        } else {
          notifyError(t("auth.ssoParamsMissingError"));
        }
      } else if(errorParams.get("errorMsg") === "Y021") {
        notifyError(t("auth.lmsGivenNameMissing"));
      } else if(errorParams.get("errorMsg") === "Y022") {
        notifyError(t("auth.lmsLastNameMissing"));
      } else if(errorParams.get("errorMsg") === "Y023") {
        notifyError(t("auth.lmsEmailMissing"));
      } else {
        notifyError(t("auth.thirdPartyLoginError"));
      }
    }  
  }, []);

  // const isTablet = useMobileAccess(1024);

  // const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // const handleOptionSelect = (idx) => {
  //   let optsCopy = JSON.parse(JSON.stringify(loginMethodOptions));
  //   optsCopy = optsCopy.map(item => ({...item, selected: false}));
  //   optsCopy[idx].selected = true;
  //   setLoginMethodOptions(optsCopy);
  //   setIsDropdownOpen(false);
  // };

  useEffect(() => {
    if (location.state && location.state.msg) {
      notifyError(location.state.msg);
      const newState = {...location.state};
      delete newState.msg
      window.history.replaceState(newState, document.title)
    }
  }, [location.state]);

  useEffect(() => {
    if (getSession().authorization) {
      if (isCSMSystem()) {
        history.push({
          pathname: "/institutions"
        });
      } else {
        history.push({
          pathname: "/home"
        });
      }
      return;
    }

    let optsCopy = JSON.parse(JSON.stringify(loginMethodOptions));
    if (isCSMSystem()) {
      optsCopy = optsCopy.map(item => ({...item, selected: false}));
      let oneLogin = optsCopy.find(item => item.val === LOGIN_METHODS.ENTRA);
      oneLogin.enabled = true;
      oneLogin.selected = true;
    } else {
      optsCopy.find(item => item.val === LOGIN_METHODS.ADFS).enabled = ssoConfig.ADFS && !checkAllEmpty(ssoConfig.ADFS);
      optsCopy.find(item => item.val === LOGIN_METHODS.SHIBBOLETH).enabled = ssoConfig.SHIBBOLETH && !checkAllEmpty(ssoConfig.SHIBBOLETH);
      optsCopy.find(item => item.val === LOGIN_METHODS.DCAM).enabled = ssoConfig.DCAM && !checkAllEmpty(ssoConfig.DCAM);
      optsCopy.find(item => item.val === LOGIN_METHODS.GOOGLE).enabled = ssoConfig.GOOGLE && !checkAllEmpty(ssoConfig.GOOGLE);
      optsCopy.find(item => item.val === LOGIN_METHODS.AZURE).enabled = ssoConfig.AZURE && !checkAllEmpty(ssoConfig.AZURE);
    }


    if (!platformSetting) {
      setLoginMethodOptions(optsCopy);
      return;
    }

    //set default login method
    optsCopy = optsCopy.map(item => ({...item, selected: false}));
    if (!!platformSetting[PLATFORM_SETTINGS.DEFAULT]) {
      const no = parseInt(platformSetting[PLATFORM_SETTINGS.DEFAULT])
      optsCopy.find((item => item.no === no)).selected = true
    }

    //check if needs to disable YuJa login
    if (!!platformSetting[PLATFORM_SETTINGS.YUJA_LOGIN]) {
      optsCopy.find(item => item.val === LOGIN_METHODS.YUJA).enabled = platformSetting[PLATFORM_SETTINGS.YUJA_LOGIN] !== "true";
    }
    if (!optsCopy.find(item => item.val === LOGIN_METHODS.YUJA).enabled) {
      optsCopy.find(item => item.val === LOGIN_METHODS.YUJA).selected = false;
    }


    //check if needs to update the login method name
    if (!!platformSetting[PLATFORM_SETTINGS.YUJA_NAME] && platformSetting[PLATFORM_SETTINGS.YUJA_NAME].trim() !== "") {
      optsCopy.find(item => item.val === LOGIN_METHODS.YUJA).abbr = platformSetting[PLATFORM_SETTINGS.YUJA_NAME];
    }
    if (!!platformSetting[PLATFORM_SETTINGS.SSONAME_ADFS] && platformSetting[PLATFORM_SETTINGS.SSONAME_ADFS].trim() !== "") {
      optsCopy.find(item => item.val === LOGIN_METHODS.ADFS).abbr = platformSetting[PLATFORM_SETTINGS.SSONAME_ADFS];
    }
    if (!!platformSetting[PLATFORM_SETTINGS.SSONAME_SHI] && platformSetting[PLATFORM_SETTINGS.SSONAME_SHI].trim() !== "") {
      optsCopy.find(item => item.val === LOGIN_METHODS.SHIBBOLETH).abbr = platformSetting[PLATFORM_SETTINGS.SSONAME_SHI];
    }
    if (!!platformSetting[PLATFORM_SETTINGS.SSONAME_DCAM] && platformSetting[PLATFORM_SETTINGS.SSONAME_DCAM].trim() !== "") {
      optsCopy.find(item => item.val === LOGIN_METHODS.DCAM).abbr = platformSetting[PLATFORM_SETTINGS.SSONAME_DCAM];
    }
    if (!!platformSetting[PLATFORM_SETTINGS.SSONAME_GOOGLE] && platformSetting[PLATFORM_SETTINGS.SSONAME_GOOGLE].trim() !== "") {
      optsCopy.find(item => item.val === LOGIN_METHODS.GOOGLE).abbr = platformSetting[PLATFORM_SETTINGS.SSONAME_GOOGLE];
    }
    if (!!platformSetting[PLATFORM_SETTINGS.SSONAME_AZURE] && platformSetting[PLATFORM_SETTINGS.SSONAME_AZURE].trim() !== "") {
      optsCopy.find(item => item.val === LOGIN_METHODS.AZURE).abbr = platformSetting[PLATFORM_SETTINGS.SSONAME_AZURE];
    }

    if (!optsCopy.find(item => item.selected)) {
      optsCopy.find(item => item.enabled).selected = true;
    }
    setLoginMethodOptions(optsCopy);
  }, [hostResource]);


  useEffect(() => {
    newPageLog("LoginPage.js");
  }, [])

  const validateForm = () => {
    let returnVal = true;
    if (!userName) {
      returnVal = false;
      setUserNameError(t("auth.invalidUsername"));
    } else {
      setUserNameError("");
    }

    if (!password) {
      returnVal = false;
      setPasswordError(t("auth.invalidPassword"));
    } else {
      setPasswordError("");
    }
    return returnVal;
  };

  const onClickSignIn = () => {

    let signInBtn = document.getElementById("sign-in-button");
    if (signInBtn && signInBtn.hasAttribute("data-disabled")) {
      return;
    }

    if (loginMethodOptions[0].selected) {
      standaloneLogin();
    } else {
      loginViaSAML();
    }
  }

  const standaloneLogin = async () => {
    if (!validateForm()) {
      return;
    }
    setGuestSession({});
    try {
      setIsProcessing(true);
      await login({userName, password, keepSignIn}, (errorCode) => {
        if (errorCode === "Y022") {
          setUserNameError(t("auth.invalidUsername"));
        } else if (errorCode === "Y023") {
          setPasswordError(t("auth.invalidPassword"));
        } else {
          notifyError(t("auth.generalLoginError"));
        }
      });
    } finally {
      setIsProcessing(false);
    }
  }

  const loginViaSAML = async() => {
    const selectedOpt = loginMethodOptions.find(opt => opt.selected);
    if (!selectedOpt) {
      return;
    }
    setIsProcessing(true);
    getSAMLRedirectURL(selectedOpt.key).then((redirectURL) => {
      if (!!redirectURL) {
        window.open(redirectURL, "_self");
      }
    }).finally(() => {
      setIsProcessing(false);
    });
  }


  const goReset = () => {
    history.push({ pathname: "/forgot" });
  }

  const togglePw = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    await setPasswordShown(!passwordShown);
    // const input = document.getElementById('input-pw');
    // input.focus();
    // input.setSelectionRange(input.value.length, input.value.length);
  }


  return (
      <>
        <div
            style={{backgroundImage: !!imageURL ? `url(${imageURL})` : undefined}}
            className={!!imageURL ? styles.imageBackground : styles.gradientBackground}
        />
        <div className={`${styles.container} ${type === SCREEN_TYPE.MOBILE ? styles.mobileContainer : ""}`}>
          <div style={{flex: 1}}/>

          <div className={styles.loginCard}>
            <div className={styles.logoContainer}>
              {!!logoImageURL
                  ?
                  <div className={styles.logoImgWrapper}>
                    <img src={logoImageURL} alt={t("auth.loginLogo")} className={styles.logoImg}/>
                  </div>
                  :
                  <YuJaLogo/>
              }

              <span className={styles.mediumText}>
                {/*{fillFormatText(WELCOME_TO_PLATFORM_TEXT, {institutionName: institutionName})}*/}
                {t("auth.welcomeToPlatform")}
              </span>
              <span className={styles.descriptionText}>{t("auth.signInToYourAccount")}</span>
            </div>


            <div style={{marginTop: 5}}>
              <span className={styles.labelText}>{t("auth.signInOptions")}</span>
              <div style={{height: 10}}/>
              <YuJaSelect
                  size={"large"}
                  style={{width: "100%"}}
                  options={loginMethodOptions.filter(item => item.enabled)}
                  getOptionValue={(item) => item.key}
                  getOptionLabel={(item) => item.abbr ? item.abbr : item.defaultName}
                  value={loginMethodOptions.find((item => item.selected))?.key ?? ""}
                  onChange={(val) => {
                    let optsCopy = JSON.parse(JSON.stringify(loginMethodOptions));
                    optsCopy = optsCopy.map(item => ({...item, selected: false}));
                    optsCopy.find(i => i.key === val).selected = true
                    setLoginMethodOptions(optsCopy);
                  }}
                  placeholder={t("auth.signInOptions")}
              />
            </div>
            {loginMethodOptions[0].selected &&
                <>
                  <div>
                    <div style={inlineStyles.rowStyle}>
                      <span className={styles.labelText}>{t("auth.usernameLabel")}</span>
                      <span style={{flex: 1}}/>
                      {!!userNameError &&
                          <>
                            <INVALID_ICON/>
                            <span className={styles.errorText}>{userNameError}</span>
                          </>
                      }
                    </div>

                    <div style={{height: 10}}/>
                    <YuJaTextField
                        size={"large"}
                        placeholder={t("auth.enterUsernamePlaceholder")}
                        value={userName}
                        onChange={(val) => {
                          setUserName(val.trim());
                          setUserNameError("");
                        }}
                        onKeyUp={event => {
                          if (event.key === 'Enter') {
                            onClickSignIn();
                          }
                        }}
                        isValid={!userNameError}
                    />
                    {/*<YuJaTextBox*/}
                    {/*    containerStyle={inlineStyles.inputBoxStyle}*/}
                    {/*    placeholder={ENTER_USERNAME_TEXT}*/}
                    {/*    value={userName}*/}
                    {/*    onChange={editUsername}*/}
                    {/*    showTooltip={true}*/}
                    {/*    onKeyUp={event => {*/}
                    {/*      if (event.key === 'Enter') {*/}
                    {/*        onClickSignIn();*/}
                    {/*      }*/}
                    {/*    }}*/}
                    {/*    isInvalid={!!userNameError}*/}
                    {/*    autoComplete="on"*/}
                    {/*/>*/}
                  </div>

                  <div>
                    <div style={inlineStyles.rowStyle}>
                      <span className={styles.labelText}>{t("auth.passwordLabel")}</span>
                      <span style={{flex: 1}}/>
                      {!!passwordError &&
                          <>
                            <INVALID_ICON/>
                            <span className={styles.errorText}>{passwordError}</span>
                          </>
                      }
                    </div>

                    <div style={{height: 10}}/>
                    <YuJaTextField
                        id={"input-pw"}
                        size={"large"}
                        type={passwordShown ? "text" : "password"}
                        placeholder={t("auth.enterPasswordPlaceholder")}
                        value={password}
                        onChange={(val) => {
                          setPassword(val.trim());
                          setPasswordError("");
                        }}
                        onKeyUp={event => {
                          if (event.key === 'Enter') {
                            onClickSignIn();
                          }
                        }}
                        isValid={!passwordError}
                        after={
                          <>
                            {!!password && (
                                <YuJaTooltip placement={"bottom"} text={passwordShown ? t("auth.hidePassword") : t("auth.showPassword")}>
                                  <Button className={styles.eyeBtn} onClick={togglePw} aria-label={passwordShown ? t("auth.hidePassword") : t("auth.showPassword")}>
                                    <img src={passwordShown ? EyeOpen : EyeClosed} alt="eye"/>
                                  </Button>
                                </YuJaTooltip>
                            )}
                          </>
                        }
                    />
                  </div>

                  <div style={inlineStyles.rowStyle}>
                    <YuJaCheckbox
                        label={t("auth.rememberMe")}
                        onChange={(checked) => setKeepSignIn(checked)}
                        checked={keepSignIn}
                        ariaLabel={t("auth.rememberMeAriaLabel") + " " + (keepSignIn ? t("accessibility.checked") : t("accessibility.unchecked"))}
                    />
                    <div style={{flex: 1}}/>
                      <Button className={styles.linkText} onClick={() => goReset()}>
                        {t("auth.forgotPassword") + '?'}
                      </Button>
                  </div>

                </>
            }


              <Button
                  id={"sign-in-button"}
                  className={styles.signInButtonNew}
                  onClick={onClickSignIn}
                  disabled={!!isProcessing}
                  focusableWhenDisabled={true}
              >
                {t("auth.signIn")}
              </Button>


            {!isCSMSystem() &&
                <Button
                    className={styles.linkText}
                    onClick={() => history.push({pathname: JOIN_PARTICIPANT_PATH})}
                    style={inlineStyles.linkStyle}
                >
                  {t("auth.joinAsParticipant")}
                </Button>
            }

          </div>
          <div style={{flex: 1}}/>
        </div>
      </>
  );
}
